import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Button } from 'primereact/button'
import './styles.scss'

const ImageCropper = ({ imageSrc, onCrop, onCancel, aspectRatio = null, minWidth = 50, minHeight = 50 }) => {
  const containerRef = useRef(null)
  const imageRef = useRef(null)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })
  const [cropBox, setCropBox] = useState({ x: 0, y: 0, width: 0, height: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragHandle, setDragHandle] = useState(null)
  const [startPos, setStartPos] = useState({ x: 0, y: 0 })
  const [startCrop, setStartCrop] = useState({ x: 0, y: 0, width: 0, height: 0 })
  const [isMoving, setIsMoving] = useState(false)

  // Initialize crop box when image loads
  useEffect(() => {
    if (imageLoaded && imageRef.current && containerRef.current) {
      // Small delay to ensure layout is complete
      const timer = setTimeout(() => {
        const img = imageRef.current
        const container = containerRef.current

        if (!img || !container) return

        const containerRect = container.getBoundingClientRect()

        // Get actual displayed image dimensions (using getBoundingClientRect for rendered size)
        const imgRect = img.getBoundingClientRect()
        const displayWidth = imgRect.width
        const displayHeight = imgRect.height

        // Calculate offset (image is centered in container)
        const offsetX = (containerRect.width - displayWidth) / 2
        const offsetY = (containerRect.height - displayHeight) / 2

        setImageSize({ width: displayWidth, height: displayHeight, offsetX, offsetY })

        // Initialize crop box to cover the entire image (100%)
        const initialWidth = displayWidth
        const initialHeight = aspectRatio ? initialWidth / aspectRatio : displayHeight

        setCropBox({
          x: offsetX,
          y: offsetY,
          width: initialWidth,
          height: initialHeight
        })
      }, 50)

      return () => clearTimeout(timer)
    }
  }, [imageLoaded, aspectRatio])

  const handleImageLoad = () => {
    setImageLoaded(true)
  }

  const getMousePos = (e) => {
    const container = containerRef.current

    if (!container) return { x: 0, y: 0 }

    const rect = container.getBoundingClientRect()

    const touch = e.touches?.[0]

    // eslint-disable-next-line
    const clientX = typeof e.clientX === 'number' ? e.clientX : (touch?.clientX ?? 0)

    // eslint-disable-next-line
    const clientY = typeof e.clientY === 'number' ? e.clientY : (touch?.clientY ?? 0)

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    }
  }

  const constrainCropBox = (newCrop) => {
    const { offsetX, offsetY, width: imgWidth, height: imgHeight } = imageSize
    const minW = minWidth
    const minH = minHeight

    let { x, y, width, height } = newCrop

    // Constrain to image bounds
    x = Math.max(offsetX, Math.min(x, offsetX + imgWidth - minW))
    y = Math.max(offsetY, Math.min(y, offsetY + imgHeight - minH))
    width = Math.max(minW, Math.min(width, offsetX + imgWidth - x))
    height = Math.max(minH, Math.min(height, offsetY + imgHeight - y))

    // Maintain aspect ratio if specified
    if (aspectRatio) {
      const currentAspect = width / height

      if (currentAspect > aspectRatio) {
        height = width / aspectRatio
        if (y + height > offsetY + imgHeight) {
          height = offsetY + imgHeight - y
          width = height * aspectRatio
        }
      } else {
        width = height * aspectRatio
        if (x + width > offsetX + imgWidth) {
          width = offsetX + imgWidth - x
          height = width / aspectRatio
        }
      }
    }

    return { x, y, width, height }
  }

  const handleMouseDown = (e, handle) => {
    e.preventDefault()
    e.stopPropagation()

    const pos = getMousePos(e)

    setIsDragging(true)
    setDragHandle(handle)
    setStartPos(pos)
    setStartCrop(cropBox)
  }

  const handleMoveStart = (e) => {
    if (e.target === containerRef.current || e.target === imageRef.current) {
      const pos = getMousePos(e)
      const { x, y, width, height } = cropBox

      // Check if click is inside crop box (but not on a handle)
      if (pos.x >= x && pos.x <= x + width && pos.y >= y && pos.y <= y + height) {
        setIsMoving(true)
        setStartPos(pos)
        setStartCrop(cropBox)
      }
    }
  }

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging && !isMoving) return

      const pos = getMousePos(e)
      const deltaX = pos.x - startPos.x
      const deltaY = pos.y - startPos.y

      const newCrop = { ...startCrop }

      if (isMoving) {
        // Move entire crop box
        newCrop.x = startCrop.x + deltaX
        newCrop.y = startCrop.y + deltaY
      } else if (dragHandle) {
        const handlers = {
          tl: () => {
            newCrop.x = startCrop.x + deltaX
            newCrop.y = startCrop.y + deltaY
            newCrop.width = startCrop.width - deltaX
            newCrop.height = startCrop.height - deltaY
          },
          tr: () => {
            newCrop.y = startCrop.y + deltaY
            newCrop.width = startCrop.width + deltaX
            newCrop.height = startCrop.height - deltaY
          },
          bl: () => {
            newCrop.x = startCrop.x + deltaX
            newCrop.width = startCrop.width - deltaX
            newCrop.height = startCrop.height + deltaY
          },
          br: () => {
            newCrop.width = startCrop.width + deltaX
            newCrop.height = startCrop.height + deltaY
          },
          t: () => {
            newCrop.y = startCrop.y + deltaY
            newCrop.height = startCrop.height - deltaY
          },
          b: () => {
            newCrop.height = startCrop.height + deltaY
          },
          l: () => {
            newCrop.x = startCrop.x + deltaX
            newCrop.width = startCrop.width - deltaX
          },
          r: () => {
            newCrop.width = startCrop.width + deltaX
          }
        }

        const handler = handlers[dragHandle]

        if (handler) handler()
      }

      setCropBox(constrainCropBox(newCrop))
    },
    [isDragging, isMoving, dragHandle, startPos, startCrop, imageSize, minWidth, minHeight, aspectRatio]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    setIsMoving(false)
    setDragHandle(null)
  }, [])

  useEffect(() => {
    if (isDragging || isMoving) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchmove', handleMouseMove)
      document.addEventListener('touchend', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('touchmove', handleMouseMove)
        document.removeEventListener('touchend', handleMouseUp)
      }
    }
  }, [isDragging, isMoving, handleMouseMove, handleMouseUp])

  const handleCrop = () => {
    if (!imageRef.current || !imageLoaded) return

    const img = imageRef.current
    const { offsetX, offsetY, width: displayWidth, height: displayHeight } = imageSize

    // Calculate crop coordinates relative to actual image
    const scaleX = img.naturalWidth / displayWidth
    const scaleY = img.naturalHeight / displayHeight

    const cropX = (cropBox.x - offsetX) * scaleX
    const cropY = (cropBox.y - offsetY) * scaleY
    const cropWidth = cropBox.width * scaleX
    const cropHeight = cropBox.height * scaleY

    // Create canvas and crop
    const canvas = document.createElement('canvas')

    canvas.width = cropWidth
    canvas.height = cropHeight

    const ctx = canvas.getContext('2d')

    ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight)

    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (blob && onCrop) {
          onCrop(blob)
        }
      },
      'image/png',
      1.0
    )
  }

  // Check if crop box covers the entire image area
  const isFullImageSelected = () => {
    if (!imageLoaded || imageSize.width === 0 || imageSize.height === 0) return false

    const tolerance = 2 // Allow 2px tolerance for floating point precision
    const coversWidth = Math.abs(cropBox.x - imageSize.offsetX) < tolerance && Math.abs(cropBox.width - imageSize.width) < tolerance
    const coversHeight = Math.abs(cropBox.y - imageSize.offsetY) < tolerance && Math.abs(cropBox.height - imageSize.height) < tolerance

    return coversWidth && coversHeight
  }

  // Reset crop box to cover entire image
  const handleReset = () => {
    if (!imageLoaded || imageSize.width === 0 || imageSize.height === 0) return

    const initialWidth = imageSize.width
    const initialHeight = aspectRatio ? initialWidth / aspectRatio : imageSize.height

    setCropBox({
      x: imageSize.offsetX,
      y: imageSize.offsetY,
      width: initialWidth,
      height: initialHeight
    })
  }

  const renderHandle = (position) => {
    const { width, height } = cropBox
    const handleSize = 6 // Half of handle width (12px / 2)

    const handleMap = {
      tl: { top: -handleSize, left: -handleSize, cursor: 'nwse-resize' },
      tr: { top: -handleSize, right: -handleSize, cursor: 'nesw-resize' },
      bl: { bottom: -handleSize, left: -handleSize, cursor: 'nesw-resize' },
      br: { bottom: -handleSize, right: -handleSize, cursor: 'nwse-resize' },
      t: { top: -handleSize, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' },
      b: { bottom: -handleSize, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' },
      l: { top: '50%', left: -handleSize, transform: 'translateY(-50%)', cursor: 'ew-resize' },
      r: { top: '50%', right: -handleSize, transform: 'translateY(-50%)', cursor: 'ew-resize' }
    }

    const handleStyle = handleMap[position] || {}

    return <div key={position} className="crop-handle" style={handleStyle} onMouseDown={(e) => handleMouseDown(e, position)} onTouchStart={(e) => handleMouseDown(e, position)} />
  }

  return (
    <div className="image-cropper-inline">
      <div ref={containerRef} className="image-cropper-content" onMouseDown={handleMoveStart} onTouchStart={handleMoveStart}>
        <img ref={imageRef} src={imageSrc} alt="Crop" onLoad={handleImageLoad} draggable={false} />

        {imageLoaded && (
          <>
            {/* Overlay with crop box cutout - only over image area */}
            <div className="crop-overlay">
              {/* Dark overlay covering image area, with transparent cutout for crop box */}
              <div
                className="crop-image-overlay"
                style={{
                  left: imageSize.offsetX,
                  top: imageSize.offsetY,
                  width: imageSize.width,
                  height: imageSize.height
                }}
              >
                {/* Top overlay */}
                {cropBox.y > imageSize.offsetY && (
                  <div
                    className="crop-overlay-section crop-overlay-top"
                    style={{
                      left: 0,
                      top: 0,
                      width: imageSize.width,
                      height: cropBox.y - imageSize.offsetY
                    }}
                  />
                )}
                {/* Bottom overlay */}
                {cropBox.y + cropBox.height < imageSize.offsetY + imageSize.height && (
                  <div
                    className="crop-overlay-section crop-overlay-bottom"
                    style={{
                      left: 0,
                      top: cropBox.y + cropBox.height - imageSize.offsetY,
                      width: imageSize.width,
                      height: imageSize.offsetY + imageSize.height - (cropBox.y + cropBox.height)
                    }}
                  />
                )}
                {/* Left overlay */}
                {cropBox.x > imageSize.offsetX && (
                  <div
                    className="crop-overlay-section crop-overlay-left"
                    style={{
                      left: 0,
                      top: cropBox.y - imageSize.offsetY,
                      width: cropBox.x - imageSize.offsetX,
                      height: cropBox.height
                    }}
                  />
                )}
                {/* Right overlay */}
                {cropBox.x + cropBox.width < imageSize.offsetX + imageSize.width && (
                  <div
                    className="crop-overlay-section crop-overlay-right"
                    style={{
                      left: cropBox.x + cropBox.width - imageSize.offsetX,
                      top: cropBox.y - imageSize.offsetY,
                      width: imageSize.offsetX + imageSize.width - (cropBox.x + cropBox.width),
                      height: cropBox.height
                    }}
                  />
                )}
              </div>

              {/* Crop box with handles */}
              <div
                className="crop-box"
                style={{
                  left: cropBox.x,
                  top: cropBox.y,
                  width: cropBox.width,
                  height: cropBox.height
                }}
              >
                <div className="crop-box-inner">
                  {/* 8 handles */}
                  {['tl', 'tr', 'bl', 'br', 't', 'b', 'l', 'r'].map((pos) => renderHandle(pos))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="image-cropper-footer">
        {isFullImageSelected() ? <Button label="Cancel" className="crop-cancel-btn" onClick={onCancel} /> : <Button label="Reset" className="crop-reset-btn" onClick={handleReset} />}
        <Button label="Crop" className="crop-confirm-btn" onClick={handleCrop} />
      </div>
    </div>
  )
}

export default ImageCropper
